import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { BarChart3, TrendingUp, Target, AlertTriangle, CheckCircle2 } from 'lucide-react';

// L10 Weekly Targets (based on monthly goals / 4 weeks)
const WEEKLY_TARGETS = {
  leads: { target: 186, min: 149 }, // 745/4 and 80% of target
  appointments: { target: 82, min: 65 },
  demos: { target: 66, min: 52 },
  sales: { target: 22, min: 17 },
  revenue: { target: 520833, min: 416666 },
  cpl: { target: 404.57, max: 484.94 },
  roas: { target: 6.66, min: 5 },
};

// Format helpers
const formatValue = (value, format) => {
  if (value === null || value === undefined) return '-';
  switch (format) {
    case 'currency':
      if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
      return `$${Number(value).toFixed(0)}`;
    case 'percent':
      return `${Number(value).toFixed(1)}%`;
    case 'multiple':
      return `${Number(value).toFixed(2)}x`;
    default:
      return Number(value).toFixed(0);
  }
};

// Get week status
const getWeekStatus = (actual, target, min, isLowerBetter = false) => {
  if (actual === null || actual === undefined) return 'neutral';
  
  if (isLowerBetter) {
    if (actual <= target) return 'on_pace';
    if (actual <= min) return 'at_risk';
    return 'below_min';
  } else {
    if (actual >= target) return 'on_pace';
    if (actual >= min) return 'at_risk';
    return 'below_min';
  }
};

const statusColors = {
  on_pace: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  at_risk: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  below_min: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  current: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-2 border-blue-500',
  neutral: 'text-slate-600 dark:text-slate-400',
};

// Week row component
const WeekRow = ({ week, weekNum, isCurrentWeek, isTotalRow = false }) => {
  const metrics = [
    { key: 'leads', value: week.leads || week.total_leads, target: WEEKLY_TARGETS.leads.target, min: WEEKLY_TARGETS.leads.min, format: 'number' },
    { key: 'appointments', value: week.appointments, target: WEEKLY_TARGETS.appointments.target, min: WEEKLY_TARGETS.appointments.min, format: 'number' },
    { key: 'demos', value: week.demos, target: WEEKLY_TARGETS.demos.target, min: WEEKLY_TARGETS.demos.min, format: 'number' },
    { key: 'sales', value: week.sales, target: WEEKLY_TARGETS.sales.target, min: WEEKLY_TARGETS.sales.min, format: 'number' },
    { key: 'revenue', value: week.revenue, target: WEEKLY_TARGETS.revenue.target, min: WEEKLY_TARGETS.revenue.min, format: 'currency' },
    { key: 'cpl', value: week.cpl, target: WEEKLY_TARGETS.cpl.target, min: WEEKLY_TARGETS.cpl.max, format: 'currency', isLowerBetter: true },
    { key: 'roas', value: week.roas, target: WEEKLY_TARGETS.roas.target, min: WEEKLY_TARGETS.roas.min, format: 'multiple' },
  ];
  
  // Overall week status (based on leads as primary metric)
  const leadsStatus = getWeekStatus(week.leads || week.total_leads, WEEKLY_TARGETS.leads.target, WEEKLY_TARGETS.leads.min);
  
  return (
    <tr className={`border-b dark:border-slate-700 ${
      isCurrentWeek ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500' : 
      isTotalRow ? 'bg-slate-100 dark:bg-slate-800 font-semibold' :
      'hover:bg-slate-50 dark:hover:bg-slate-800/50'
    }`}>
      <td className={`px-3 py-3 text-sm font-medium whitespace-nowrap ${isCurrentWeek ? 'text-blue-700 dark:text-blue-400' : 'dark:text-white'}`}>
        <div className="flex items-center gap-2">
          {isTotalRow ? (
            <>
              <Target className="h-4 w-4 text-primary" />
              <span>MTD TOTAL</span>
            </>
          ) : (
            <>
              {isCurrentWeek && <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />}
              <span>Week {weekNum}</span>
              {isCurrentWeek && <Badge className="bg-blue-500 text-xs ml-1">Current</Badge>}
            </>
          )}
        </div>
      </td>
      
      {/* Date range */}
      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
        {isTotalRow ? '-' : (week.start_date ? `${week.start_date} - ${week.end_date}` : '-')}
      </td>
      
      {/* Metrics */}
      {metrics.map(({ key, value, target, min, format, isLowerBetter }) => {
        const status = isTotalRow ? 'neutral' : getWeekStatus(value, target, min, isLowerBetter);
        
        return (
          <td 
            key={key} 
            className={`px-3 py-2 text-center text-sm ${
              isTotalRow ? 'font-bold' : statusColors[status]
            }`}
          >
            {formatValue(value, format)}
          </td>
        );
      })}
      
      {/* Delta vs Target (only for non-total rows) */}
      <td className={`px-3 py-2 text-center text-xs ${
        isTotalRow ? '' :
        leadsStatus === 'on_pace' ? 'text-green-600 dark:text-green-400' :
        leadsStatus === 'at_risk' ? 'text-yellow-600 dark:text-yellow-400' :
        'text-red-600 dark:text-red-400'
      }`}>
        {isTotalRow ? '-' : (
          <span>
            {(week.leads || week.total_leads || 0) - WEEKLY_TARGETS.leads.target >= 0 ? '+' : ''}
            {((week.leads || week.total_leads || 0) - WEEKLY_TARGETS.leads.target).toFixed(0)}
          </span>
        )}
      </td>
    </tr>
  );
};

// Main Enhanced Weekly Pacing Grid component
export const EnhancedWeeklyPacingGrid = ({ weeklyData, mtdData, loading }) => {
  if (loading) {
    return (
      <Card className="dark:bg-slate-800/60" data-testid="enhanced-weekly-pacing-loading">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            Weekly Pacing Grid
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-12 bg-slate-200 dark:bg-slate-700 rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const weeks = weeklyData || [];
  const metrics = mtdData?.metrics || {};
  
  // Find current week
  const currentWeekIndex = weeks.findIndex(w => w.is_current_week);
  
  // Calculate MTD totals
  const mtdTotals = {
    leads: metrics.leads?.actual || weeks.reduce((sum, w) => sum + (w.leads || w.total_leads || 0), 0),
    appointments: metrics.appointments?.actual || metrics.appointments_set?.actual || weeks.reduce((sum, w) => sum + (w.appointments || 0), 0),
    demos: metrics.demos?.actual || weeks.reduce((sum, w) => sum + (w.demos || 0), 0),
    sales: metrics.sales?.actual || weeks.reduce((sum, w) => sum + (w.sales || 0), 0),
    revenue: metrics.revenue?.actual || weeks.reduce((sum, w) => sum + (w.revenue || 0), 0),
    cpl: metrics.cpl?.actual,
    roas: metrics.roas?.actual,
  };
  
  // Calculate projected month-end based on current pace
  const completedWeeks = weeks.filter((w, i) => i <= currentWeekIndex).length || 1;
  const weeksInMonth = 4;
  const projectedEndOfMonth = {
    leads: Math.round((mtdTotals.leads / completedWeeks) * weeksInMonth),
    sales: Math.round((mtdTotals.sales / completedWeeks) * weeksInMonth),
    revenue: Math.round((mtdTotals.revenue / completedWeeks) * weeksInMonth),
  };

  const columns = [
    { key: 'week', label: 'Week', width: 'w-28' },
    { key: 'dates', label: 'Dates', width: 'w-32' },
    { key: 'leads', label: 'Leads' },
    { key: 'appointments', label: 'Appts' },
    { key: 'demos', label: 'Demos' },
    { key: 'sales', label: 'Sales' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'cpl', label: 'CPL' },
    { key: 'roas', label: 'ROAS' },
    { key: 'delta', label: 'Δ Target' },
  ];

  return (
    <Card className="dark:bg-slate-800/60" data-testid="enhanced-weekly-pacing-grid">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            Weekly Pacing Grid
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {completedWeeks} of {weeksInMonth} weeks
            </Badge>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Weekly performance with appointments, demos, sales, and efficiency metrics
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {/* Weekly Targets Reference */}
        <div className="px-4 py-2 bg-slate-50 dark:bg-slate-900/30 border-b dark:border-slate-700 text-xs text-muted-foreground">
          <strong className="text-slate-700 dark:text-slate-300">Weekly Targets:</strong>
          <span className="ml-3">Leads: {WEEKLY_TARGETS.leads.target} (min {WEEKLY_TARGETS.leads.min})</span>
          <span className="ml-3">Appts: {WEEKLY_TARGETS.appointments.target}</span>
          <span className="ml-3">Sales: {WEEKLY_TARGETS.sales.target}</span>
          <span className="ml-3">Revenue: {formatValue(WEEKLY_TARGETS.revenue.target, 'currency')}</span>
          <span className="ml-3">CPL: ≤${WEEKLY_TARGETS.cpl.target}</span>
          <span className="ml-3">ROAS: ≥{WEEKLY_TARGETS.roas.target}x</span>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead>
              <tr className="border-b dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                {columns.map(col => (
                  <th 
                    key={col.key} 
                    className={`px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide ${col.width || ''} ${['week', 'dates'].includes(col.key) ? 'text-left' : 'text-center'}`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Weekly Rows */}
              {weeks.map((week, idx) => (
                <WeekRow 
                  key={idx} 
                  week={week} 
                  weekNum={idx + 1}
                  isCurrentWeek={week.is_current_week}
                />
              ))}
              
              {/* MTD Total Row */}
              <WeekRow 
                week={mtdTotals}
                weekNum={0}
                isCurrentWeek={false}
                isTotalRow={true}
              />
              
              {weeks.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">
                    No weekly data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Projection Footer */}
        <div className="p-4 border-t dark:border-slate-700 bg-gradient-to-r from-blue-50 to-transparent dark:from-blue-900/20">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-blue-500" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Month-End Projection (Based on Current Pace)
            </span>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Projected Leads: </span>
              <span className={`font-bold ${projectedEndOfMonth.leads >= WEEKLY_TARGETS.leads.target * weeksInMonth ? 'text-green-600' : 'text-yellow-600'}`}>
                {projectedEndOfMonth.leads}
              </span>
              <span className="text-xs text-muted-foreground ml-1">
                (target: {WEEKLY_TARGETS.leads.target * weeksInMonth})
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Projected Sales: </span>
              <span className={`font-bold ${projectedEndOfMonth.sales >= WEEKLY_TARGETS.sales.target * weeksInMonth ? 'text-green-600' : 'text-yellow-600'}`}>
                {projectedEndOfMonth.sales}
              </span>
              <span className="text-xs text-muted-foreground ml-1">
                (target: {WEEKLY_TARGETS.sales.target * weeksInMonth})
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Projected Revenue: </span>
              <span className={`font-bold ${projectedEndOfMonth.revenue >= WEEKLY_TARGETS.revenue.target * weeksInMonth ? 'text-green-600' : 'text-yellow-600'}`}>
                {formatValue(projectedEndOfMonth.revenue, 'currency')}
              </span>
              <span className="text-xs text-muted-foreground ml-1">
                (target: {formatValue(WEEKLY_TARGETS.revenue.target * weeksInMonth, 'currency')})
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EnhancedWeeklyPacingGrid;
